import React from 'react'

import { getBreakdownLabel } from 'features/charts-v2/components/breakdown-selection-utils'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import type { SankeyGraph } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { SankeyChartView } from 'features/charts-v2/strategies/SankeyChartView'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
  type TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

const VOLUME_LABEL = 'Mentions'
const RECORD_VOLUME_LABEL = 'Records'

/**
 * Sankey (flow) chart strategy.
 *
 * Follows volume across 2-4 columns, one per breakdown (a grouped breakdown
 * takes two: group, then member). The whole graph is one "datapoint" of the
 * transformed data, since nodes and links only make sense together.
 */
export class SankeyChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.SANKEY
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    if (!props.data) return { chartData: [] }
    const graph = dataTransformationService.transformForSankey(
      props.data,
      props.selectedBreakdowns ?? [],
    )
    return {
      chartData:
        graph.links.length > 0
          ? [graph as unknown as TransformedDataPoint]
          : [],
    }
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      selectedBreakdowns = [],
      selectedMetrics,
      onSegmentClick,
      colorMap,
      fontSizePx = 13,
      showDatapointLabels = true,
      flowColorMode = 'origin',
    } = props

    const graph = transformedData.chartData?.[0] as unknown as
      | SankeyGraph
      | undefined
    if (!graph) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    return (
      <SankeyChartView
        graph={graph}
        display={{
          flowColorMode,
          fontSizePx,
          showNodeCounts: showDatapointLabels,
          volumeLabel:
            selectedMetrics[0] === METRICS.RECORD_COUNT.id
              ? RECORD_VOLUME_LABEL
              : VOLUME_LABEL,
          columnLabels: graph.columns.map(({ breakdownIndex, level }) => {
            const label = getBreakdownLabel(
              selectedBreakdowns[breakdownIndex] ?? null,
            )
            return level === 'group' ? `${label} group` : label
          }),
          commonMargin: this.getCommonMargin(),
        }}
        colorMap={colorMap}
        onSegmentClick={onSegmentClick}
      />
    )
  }

  public getTitle(): string {
    return 'Flow'
  }
}
